import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Divider,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material';
import { confidenceColor } from '../../utils/stagingConstants';

/**
 * Displays the confidence score breakdown for a match candidate.
 * Uses the scoreBreakdown from the backend when available,
 * falls back to local computation only for legacy data.
 */
const ConfidenceBreakdown = ({ candidate, staged }) => {
  // Direct match shortcut — no breakdown needed
  if (candidate.matchReason === 'Direct ID Match') {
    return (
      <Box sx={{ p: 2.5, maxWidth: 420, minWidth: 340 }}>
        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          Direct ID Match
        </Typography>
        <Alert severity='success' sx={{ mb: 1 }}>
          This voyage was submitted as a PUT update targeting this specific
          voyage by ID. The match is guaranteed at 100% confidence — no scoring
          algorithm was used.
        </Alert>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='body2' fontWeight='bold'>
            Confidence
          </Typography>
          <Chip
            label='100%'
            size='small'
            color='success'
            sx={{ fontWeight: 'bold' }}
          />
        </Box>
      </Box>
    );
  }

  // Prefer backend-provided breakdown
  const factors = candidate.scoreBreakdown
    ? candidate.scoreBreakdown.map((entry) => ({
        label: entry.name,
        weight: entry.weight,
        earned: entry.earned,
        skipped: entry.weight === 0,
        detail: entry.note || '',
      }))
    : computeScoreBreakdownLocally(staged, candidate);

  const effectiveMax = factors.reduce(
    (sum, f) => sum + (f.skipped ? 0 : f.weight),
    0
  );
  const totalEarned = factors.reduce((sum, f) => sum + f.earned, 0);
  const confidencePercent =
    effectiveMax > 0 ? Math.round((totalEarned / effectiveMax) * 100) : 0;

  return (
    <Box sx={{ p: 2.5, maxWidth: 420, minWidth: 340 }}>
      <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
        Match Confidence Breakdown
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Score is calculated by comparing key fields between the staged (API)
        voyage and this candidate.
      </Typography>

      <Stack spacing={1.5}>
        {factors.map((f, i) => (
          <Box key={i} sx={{ opacity: f.skipped ? 0.5 : 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 0.25,
              }}
            >
              <Typography variant='body2' fontWeight='medium'>
                {f.label}
              </Typography>
              <Typography
                variant='body2'
                fontWeight='bold'
                color={
                  f.earned === f.weight
                    ? 'success.main'
                    : f.earned > 0
                      ? 'warning.main'
                      : 'text.disabled'
                }
              >
                {f.skipped ? '—' : `${Math.round(f.earned)} / ${f.weight}`}
              </Typography>
            </Box>
            {!f.skipped && (
              <LinearProgress
                variant='determinate'
                value={(f.earned / f.weight) * 100}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  mb: 0.25,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    backgroundColor:
                      f.earned === f.weight
                        ? 'success.main'
                        : f.earned > 0
                          ? 'warning.main'
                          : 'grey.400',
                  },
                }}
              />
            )}
            <Typography variant='caption' color='text.secondary'>
              {f.detail}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='body2' fontWeight='bold'>
          Total Score
        </Typography>
        <Chip
          label={`${confidencePercent}%`}
          size='small'
          color={confidenceColor(confidencePercent / 100)}
          sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
        />
      </Box>
      <Typography variant='caption' color='text.secondary'>
        {Math.round(totalEarned)} / {effectiveMax} points (threshold: 50%)
      </Typography>
    </Box>
  );
};

/**
 * Legacy local computation fallback — used when backend doesn't provide scoreBreakdown.
 */
function computeScoreBreakdownLocally(staged, candidate) {
  const WEIGHT_IMO = 30;
  const WEIGHT_DEPARTURE_PORT = 10;
  const WEIGHT_DESTINATION_PORT = 10;
  const WEIGHT_PORT_OPERATOR = 5;
  const WEIGHT_ETD = 40;
  const WEIGHT_VOYAGE_NUMBER = 5;
  const MAX_ETD_DIFF_DAYS = 20;
  const ETD_HARD_EXCLUSION_DAYS = 100;

  const factors = [];

  // IMO
  const imoPlaceholder = staged.imo === 'TBN' || candidate.imo === 'TBN';
  if (imoPlaceholder) {
    factors.push({
      label: 'Ship (IMO)',
      weight: WEIGHT_IMO,
      earned: 0,
      skipped: true,
      detail: 'Skipped — placeholder "TBN"',
    });
  } else {
    const match = staged.imo && candidate.imo && staged.imo === candidate.imo;
    factors.push({
      label: 'Ship (IMO)',
      weight: WEIGHT_IMO,
      earned: match ? WEIGHT_IMO : 0,
      skipped: false,
      detail: match
        ? 'Exact match'
        : `${staged.imo || '?'} ≠ ${candidate.imo || '?'}`,
    });
  }

  // Departure port
  const depMatch =
    staged.departurePort &&
    candidate.departurePort &&
    staged.departurePort === candidate.departurePort;
  factors.push({
    label: 'Departure Port',
    weight: WEIGHT_DEPARTURE_PORT,
    earned: depMatch ? WEIGHT_DEPARTURE_PORT : 0,
    skipped: false,
    detail: depMatch
      ? 'Exact match'
      : `${staged.departurePort || '?'} ≠ ${candidate.departurePort || '?'}`,
  });

  // Destination port
  const destMatch =
    staged.destinationPort &&
    candidate.destinationPort &&
    staged.destinationPort === candidate.destinationPort;
  factors.push({
    label: 'Destination Port',
    weight: WEIGHT_DESTINATION_PORT,
    earned: destMatch ? WEIGHT_DESTINATION_PORT : 0,
    skipped: false,
    detail: destMatch
      ? 'Exact match'
      : `${staged.destinationPort || '?'} ≠ ${candidate.destinationPort || '?'}`,
  });

  // Port operator
  const poMatch =
    staged.portOperatorId &&
    candidate.portOperatorId &&
    staged.portOperatorId === candidate.portOperatorId;
  factors.push({
    label: 'Port Operator',
    weight: WEIGHT_PORT_OPERATOR,
    earned: poMatch ? WEIGHT_PORT_OPERATOR : 0,
    skipped: false,
    detail: poMatch ? 'Exact match' : 'Different operators',
  });

  // ETD proximity
  let etdEarned = 0;
  let etdDetail = 'No ETD available';
  if (staged.actualETD && candidate.actualETD) {
    const stagedDate = new Date(staged.actualETD);
    const candidateDate = new Date(candidate.actualETD);
    const diffMs = Math.abs(stagedDate - candidateDate);
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays > ETD_HARD_EXCLUSION_DAYS) {
      etdDetail = `${diffDays} days apart (excluded — exceeds ${ETD_HARD_EXCLUSION_DAYS} day limit)`;
    } else if (diffDays <= MAX_ETD_DIFF_DAYS) {
      const ratio = diffDays / (MAX_ETD_DIFF_DAYS + 1);
      etdEarned = WEIGHT_ETD * (1 - ratio * ratio);
      etdDetail =
        diffDays === 0
          ? 'Same day'
          : `${diffDays} day${diffDays > 1 ? 's' : ''} apart (quadratic decay)`;
    } else {
      etdDetail = `${diffDays} days apart (exceeds ±${MAX_ETD_DIFF_DAYS} day scoring window)`;
    }
  }
  factors.push({
    label: 'ETD Proximity',
    weight: WEIGHT_ETD,
    earned: Math.round(etdEarned * 100) / 100,
    skipped: false,
    detail: etdDetail,
  });

  // Voyage number
  const vnMatch =
    staged.voyageNumber &&
    candidate.voyageNumber &&
    staged.voyageNumber.toLowerCase() === candidate.voyageNumber.toLowerCase();
  factors.push({
    label: 'Voyage Number',
    weight: WEIGHT_VOYAGE_NUMBER,
    earned: vnMatch ? WEIGHT_VOYAGE_NUMBER : 0,
    skipped: false,
    detail: vnMatch
      ? 'Exact match'
      : `${staged.voyageNumber || '?'} ≠ ${candidate.voyageNumber || '?'}`,
  });

  return factors;
}

export default ConfidenceBreakdown;
